import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { NAV } from "@/constants/testIds";
import { Menu, X, Printer, Users } from "lucide-react";
import { useState, useEffect } from "react";
import ThemeToggle from "@/components/ThemeToggle";
import { api } from "@/lib/api";
import { devLog } from "@/lib/devLog";
import { useLiveUsers } from "@/lib/analytics";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const { count: liveUsers, exact: liveUsersExact } = useLiveUsers();

  const isStaff = user?.role === "admin" || user?.role === "super_admin" || user?.role === "staff";
  // Poll the inbox unread count every 30s for staff/admin
  useEffect(() => {
    if (!isStaff) return;
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/admin/inbox/unread-count");
        if (!stop) setUnread(data.unread || 0);
      } catch (err) { devLog.debug("unread-count poll failed", err); }
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => { stop = true; clearInterval(t); };
  }, [isStaff]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const linkBase =
    "px-3 py-2 text-sm uppercase tracking-[0.15em] font-bold transition-colors hover:text-[#059669]";
  const active = "text-[#059669]";

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-[#E5E5E5]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2" data-testid={NAV.logo}>
          <div className="w-9 h-9 bg-[#0A0A0A] flex items-center justify-center">
            <Printer className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="leading-none">
            <div className="font-display font-black text-lg">CTP KENYA</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#525252]">Print · Bind · Laminate</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <NavLink to="/" end className={({ isActive }) => `${linkBase} ${isActive ? active : "text-[#0A0A0A]"}`} data-testid={NAV.home}>
            Home
          </NavLink>
          <NavLink to="/order" className={({ isActive }) => `${linkBase} ${isActive ? active : "text-[#0A0A0A]"}`} data-testid={NAV.order}>
            New Order
          </NavLink>
          <NavLink to="/track" className={({ isActive }) => `${linkBase} ${isActive ? active : "text-[#0A0A0A]"}`} data-testid={NAV.track}>
            Track
          </NavLink>
          <NavLink to="/how-it-works" className={({ isActive }) => `${linkBase} ${isActive ? active : "text-[#0A0A0A]"}`} data-testid="nav-how-it-works">
            How it works
          </NavLink>
          <NavLink to="/visit" className={({ isActive }) => `${linkBase} ${isActive ? active : "text-[#0A0A0A]"}`} data-testid="nav-visit">
            Visit
          </NavLink>
          {user?.role === "admin" || user?.role === "super_admin" || user?.role === "staff" ? (
            <NavLink to="/admin" className={({ isActive }) => `${linkBase} ${isActive ? active : "text-[#0A0A0A]"} relative`} data-testid={NAV.admin}>
              {user.role === "staff" ? "Staff" : "Admin"}
              {unread > 0 && (
                <span data-testid="nav-inbox-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-[#059669] text-white text-[10px] mono font-bold inline-flex items-center justify-center">
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </NavLink>
          ) : null}
          {user && user.role === "customer" && (
            <NavLink to="/dashboard" className={({ isActive }) => `${linkBase} ${isActive ? active : "text-[#0A0A0A]"}`} data-testid={NAV.dashboard}>
              My Orders
            </NavLink>
          )}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {liveUsers > 0 && (
            <div
              data-testid="live-users-badge"
              title={
                liveUsersExact
                  ? `EXACT: ${liveUsers} ${liveUsers === 1 ? "person is" : "people are"} on the portal right now`
                  : `${liveUsers} ${liveUsers === 1 ? "person" : "people"} using the portal right now`
              }
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 border text-[10px] mono font-bold uppercase tracking-[0.15em] ${liveUsersExact ? "border-[#F59E0B] bg-[#F59E0B]/10 text-[#B45309]" : "border-[#059669] bg-[#059669]/10 text-[#059669]"}`}
            >
              <span className="relative flex h-2 w-2">
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${liveUsersExact ? "bg-[#F59E0B]" : "bg-[#059669]"}`}></span>
                <span className={`relative inline-flex rounded-full h-2 w-2 ${liveUsersExact ? "bg-[#F59E0B]" : "bg-[#059669]"}`}></span>
              </span>
              <Users className="w-3 h-3" strokeWidth={2.5} />
              {liveUsers}
              {liveUsersExact && <span className="ml-1 text-[9px] opacity-80">EXACT</span>}
            </div>
          )}
          <ThemeToggle />
          {user ? (
            <>
              <span className="text-xs uppercase tracking-[0.15em] text-[#525252]">{user.name?.split(" ")[0] || user.email}</span>
              <button onClick={handleLogout} data-testid={NAV.logout} className="px-4 py-2 text-sm uppercase tracking-[0.15em] font-bold border-2 border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors">
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid={NAV.login} className="px-4 py-2 text-sm uppercase tracking-[0.15em] font-bold hover:text-[#059669]">
                Login
              </Link>
              <Link to="/signup" data-testid={NAV.signup} className="px-4 py-2 text-sm uppercase tracking-[0.15em] font-bold bg-[#0A0A0A] text-white hover:bg-[#059669]">
                Sign up
              </Link>
            </>
          )}
        </div>

        <button onClick={() => setOpen((v) => !v)} className="md:hidden w-10 h-10 flex items-center justify-center" data-testid="nav-mobile-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#E5E5E5] bg-white">
          <div className="px-4 py-3 flex flex-col gap-1">
            <NavLink to="/" end onClick={() => setOpen(false)} className={`${linkBase} text-[#0A0A0A]`}>Home</NavLink>
            <NavLink to="/order" onClick={() => setOpen(false)} className={`${linkBase} text-[#0A0A0A]`}>New Order</NavLink>
            <NavLink to="/track" onClick={() => setOpen(false)} className={`${linkBase} text-[#0A0A0A]`}>Track Order</NavLink>
            <NavLink to="/how-it-works" onClick={() => setOpen(false)} className={`${linkBase} text-[#0A0A0A]`}>How it works</NavLink>
            <NavLink to="/visit" onClick={() => setOpen(false)} className={`${linkBase} text-[#0A0A0A]`}>Visit the shop</NavLink>
            {(user?.role === "admin" || user?.role === "super_admin" || user?.role === "staff") && <NavLink to="/admin" onClick={() => setOpen(false)} className={`${linkBase} text-[#0A0A0A]`}>{user.role === "staff" ? "Staff" : "Admin"}</NavLink>}
            {user && user.role === "customer" && <NavLink to="/dashboard" onClick={() => setOpen(false)} className={`${linkBase} text-[#0A0A0A]`}>My Orders</NavLink>}
            <div className="border-t border-[#E5E5E5] mt-2 pt-2">
              <div className="flex items-center justify-between px-3 pb-2">
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252]">Theme</span>
                <ThemeToggle />
              </div>
              {user ? (
                <button onClick={handleLogout} className="w-full text-left px-3 py-2 text-sm uppercase tracking-[0.15em] font-bold">Logout</button>
              ) : (
                <div className="flex gap-2 px-3">
                  <Link to="/login" onClick={() => setOpen(false)} className="flex-1 px-4 py-2 text-sm uppercase tracking-[0.15em] font-bold border-2 border-[#0A0A0A] text-center">Login</Link>
                  <Link to="/signup" onClick={() => setOpen(false)} className="flex-1 px-4 py-2 text-sm uppercase tracking-[0.15em] font-bold bg-[#0A0A0A] text-white text-center">Sign up</Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
